import * as React from "react";
import { connect } from "react-redux";
import { match } from "react-router";
import { mapDispatchToProps } from "../../redux/actions";
import { addNotification, removeNotification } from "../../api-utilities/notification-tools";
import {
  getValidationError,
  getSelectBoxOptions,
  getInputCSS,
  getFilteredValidationErrors,
  getNewDropdownList,
  getNewValidationErrorsList
} from "../../utilities/pageUtils";
import { updateInputField } from "../../api-calls/readApiService";
import { isEmptyObject } from "../../utilities/helpers";

// expected props
interface IProps {
  updateRedux: any;
  match: match<any>;
  title: string;
  componentData: any;
  userInputs: any;
  targetVertex: string;
}
// local state
interface IState {
  userInputs: any;
  displayFields: any;
  dropdownOptions: any[];
  validationErrors: any[];
  lastUpdatedTextbox: string;
}
class TempsSystemCoilInputs extends React.Component<IProps, IState> {
  public state: IState = {
    userInputs: null,
    displayFields: null,
    dropdownOptions: [],
    validationErrors: [],
    lastUpdatedTextbox: ""
  };
  async componentDidMount() {
    const componentData = this.props.componentData;

    if (!isEmptyObject(componentData)) {
      this.setState({
        userInputs: this.props.userInputs,
        displayFields: componentData.uiDataContainer.displayFields,
        dropdownOptions: componentData.dropdownOptions,
        validationErrors: componentData.validationErrors
      });
      this.checkForPreheatDryBulbError(componentData.uiDataContainer.displayFields)
    }
  }

  //This function gets called on every keystroke in a textbox.
  public async updateTextboxValue(fieldName: string, newValue: string) {
    const newState = Object.assign({}, this.state.userInputs, { [fieldName]: newValue });
    this.setState({ userInputs: newState, lastUpdatedTextbox: fieldName });
  }

  //This gets called by textbox onBlur events
  public async saveTextboxValue(fieldName: string, newValue: string) {
    //If field value hasn't changed, don't call the server
    if (this.state.lastUpdatedTextbox !== fieldName) {
      return;
    }
    this.saveInputValue(fieldName, newValue);
  }

  //Save new value to redis and DB
  public async saveInputValue(fieldName: string, newValue: string) {
    const { projectId, unitId } = this.props.match.params;
    this.removeValidationError(fieldName);
    let result = await updateInputField("updateCoil", projectId, unitId, fieldName, newValue, this.props.targetVertex);

    if (result.success) {
      //If there are any errors in the response, add them to state
      if (result.data.validationErrors != null) {
        this.addValidationErrors(result.data.validationErrors);
      }

      //If there are any dropdownLists present in the response, then update those in state
      if (result.data.dropdownOptions != null) {
        this.updateDropdowns(result.data.dropdownOptions);
      }

      //If uiDataContainer was updated with new values, then update it in state
      if (result.data.uiDataContainer != null) {
        this.setState({
          userInputs: result.data.uiDataContainer.coilInputs,
          displayFields: result.data.uiDataContainer.displayFields
        });
        this.checkForPreheatDryBulbError(this.state.displayFields)
      }
    }
  }
  private checkForPreheatDryBulbError(displayFields: any){
    if (displayFields.showPreheatDryBulbWarning) {
      const notification = {
        id: "preheatdrybulb",
        notificationType: "warning",
        title: "Warning",
        content: <div className="nf-div">The preheat coil is designed below 40°F and is approaching the freezing point of water. 
        Innovent recommends designing hydronic coils with glycol or increase the preheat desired LAT to prevent freezing.</div>
      };
      addNotification(notification);
    } else {
      removeNotification("preheatdrybulb");
    }
  }
  //Check for errors on this textbox/selectbox, and return the appropriate CSS className
  public getCSS(fieldName: string, type: string) {
    return getInputCSS(this.state.validationErrors, fieldName, type);
  }

  //Update dropdowns with new options from the server
  public updateDropdowns(newDropdownOptions: any) {
    let newList = getNewDropdownList(this.state.dropdownOptions, newDropdownOptions);
    this.setState({ dropdownOptions: newList });
  }

  //Add any validation errors we got from the server to state
  public addValidationErrors(validationErrors: any[]) {
    let newList = getNewValidationErrorsList(this.state.validationErrors, validationErrors);
    this.setState({ validationErrors: newList });
  }

  public getValidationError(fieldName: string) {
    //Call the validation method from pageUtils
    return getValidationError(this.state.validationErrors, fieldName, "validation-err-right");
  }

  //Whenever we update a field, remove any previous error for that field
  //Also reset the lastUpdatedTextbox in state
  public removeValidationError(fieldName: string) {
    let newArray = getFilteredValidationErrors(this.state.validationErrors, fieldName);
    this.setState({ validationErrors: newArray, lastUpdatedTextbox: "" });
  }

  //------------------------------------------------------------------------------------------
  public render() {
    const savedValues = this.state.userInputs;
    if (!savedValues) {
      return null;
    }
    return (
    <div>
        <div className="input-heading">{this.props.title}</div>

        <div className="input-fields">
            <div>
                <div className="system-input-label">Fluid Type</div>
                <div className="input-textbox">
                <select
                    className={this.getCSS("coilFluidType", "selectbox")}
                    value={savedValues.coilFluidType}
                    onChange={(event) => this.saveInputValue("coilFluidType", event.currentTarget.value)}
                >
                    {getSelectBoxOptions(this.state.dropdownOptions, "coilFluidTypeOptions")}
                </select>
                </div>
                {this.getValidationError("coilFluidType")}
            </div>

            {savedValues.coilFluidType !== "Water" ? (   
            <div>
                <div className="system-input-label">Glycol %</div>
                <div className="input-textbox">
                <input type="text"
                    className={this.getCSS("glycolPercent", "textbox")}
                    value={savedValues.glycolPercent}
                    onChange={(event) => this.updateTextboxValue("glycolPercent", event.currentTarget.value)}
                    onBlur={(event) => this.saveTextboxValue("glycolPercent", event.currentTarget.value)}
                />
                </div>
                {this.getValidationError("glycolPercent")}
            </div>
            ) : null}

            <div>
                <div className="system-input-label">Entering Fluid Temp</div>
                <div className="input-textbox">
                <input
                    type="text"
                    className={this.getCSS("enteringFluidTemperature", "textbox")}
                    value={savedValues.enteringFluidTemperature}
                    onChange={(event) => this.updateTextboxValue("enteringFluidTemperature", event.currentTarget.value)}
                    onBlur={(event) => this.saveTextboxValue("enteringFluidTemperature", event.currentTarget.value)}
                />
                </div>
                {this.getValidationError("enteringFluidTemperature")}
            </div>

            <div>
                <div className="system-input-label">Leaving Fluid Temp</div>
                <div className="input-textbox">
                <input
                    type="text"
                    className={this.getCSS("heatingEnteringFluidTemp", "textbox")}
                    value={savedValues.leavingFluidTemperature}
                    onChange={(event) => this.updateTextboxValue("leavingFluidTemperature", event.currentTarget.value)}
                    onBlur={(event) => this.saveTextboxValue("leavingFluidTemperature", event.currentTarget.value)}
                />
                </div>
                {this.getValidationError("leavingFluidTemperature")}
            </div>
        </div>
    </div>
    );
  }
}

//------------------ Redux ----------------------------
function mapStateToProps(state: any) {
  return {};
}
export default connect(mapStateToProps, mapDispatchToProps)(TempsSystemCoilInputs);
